import { Outlet } from 'react-router-dom';

import { SidebarInset, SidebarProvider } from '@/components/cargofl-ui';
import { AppSidebar } from '@/components/shell/app-sidebar';
import { OnboardingGuide } from '@/components/shell/onboarding-guide';
import { PageErrorBoundary } from '@/components/shell/page-error-boundary';
import { TopBar } from '@/components/shell/top-bar';
import { pendingAtStage, useMyCurrentSelf, usePendingAssessments } from '@/lib/assessments-api';
import { useGoalSet } from '@/lib/goals-api';
import type { NavBadges } from '@/lib/nav';

/**
 * Authenticated app shell: sidebar + top bar wrapping the routed page.
 *
 * Route protection lives in <RequireAuth> (see App.tsx), not here, so this can
 * assume a signed-in user.
 */
export function AppShell() {
  // One request feeds every review badge - the server already returns only what
  // this user may act on, so the counts cannot show work that is not theirs.
  const { data: pending } = usePendingAssessments();
  const { data: goalSet } = useGoalSet('me');
  const { data: mySelf } = useMyCurrentSelf();

  const badges: NavBadges = {
    managerReviewsPending: countFor(pending, 'MANAGER'),
    hrReviewsPending: countFor(pending, 'HR'),
    managementReviewsPending: countFor(pending, 'MANAGEMENT'),
    // Nothing to acknowledge any more — goals are live when published. The badge stays
    // for the manager's side of it: a set of your own still sitting in DRAFT.
    goalsPendingAcceptance:
      goalSet?.status === 'DRAFT' ? { count: 1, urgent: false } : undefined,
    /**
     * This person's own assessment needs them: either the window is open and it is
     * unsubmitted, or their manager has rated it and it is waiting on their answer.
     *
     * Both, because they are the two moments the chain is blocked on this one person
     * and neither shows up in `pending`, which only covers reviews they owe others.
     * Acceptance is not `actionable` - that means "you can still edit and submit" - so
     * counting only that left the badge dark at exactly the moment somebody was holding
     * their own appraisal up.
     */
    selfAssessmentOpen:
      mySelf?.actionable || (mySelf?.exists && mySelf.awaitingMyAcceptance)
        ? {
            count: 1,
            urgent: mySelf.exists ? (mySelf.closesInDays ?? 99) <= 2 && mySelf.actionable : false,
          }
        : undefined,
  };

  return (
    <SidebarProvider>
      <AppSidebar badges={badges} />
      <SidebarInset>
        <TopBar />
        {/* `min-w-0` for the same reason SidebarInset needs it: without it a wide
            table inflates the flex child and re-breaks horizontal scrolling one
            level down. */}
        <div className="min-w-0 flex-1">
          {/* Around the outlet, not the shell: a page that throws must not take the
              sidebar with it, or there is no way out but a reload. */}
          <PageErrorBoundary>
            <Outlet />
          </PageErrorBoundary>
        </div>
      </SidebarInset>
      <OnboardingGuide />
    </SidebarProvider>
  );
}

function countFor(
  pending: ReturnType<typeof usePendingAssessments>['data'],
  stage: 'MANAGER' | 'HR' | 'MANAGEMENT',
): { count: number; urgent: boolean } | undefined {
  if (!pending) return undefined;
  // pendingAtStage, not a filter written again here: this count is a promise about how
  // many rows the page behind the link will show, and the two have to be one decision.
  const mine = pendingAtStage(pending, stage);
  if (mine.length === 0) return undefined;
  // Red only when the 3-day SLA has actually been missed. A pending review is
  // normal work; colouring it as urgent from the start makes the badge noise.
  return { count: mine.length, urgent: mine.some((a) => a.managerOverdue) };
}
